'use strict';
import { SupportedOperations } from '../model/enumerations';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.ExternalServiceManager
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * Provides list of methods for working with external Rest Services.
 */
ExternalServiceManager.$inject = ['$q', 'mx.internationalization', 'mx.SolutionBuilderAgent.Http'];
export function ExternalServiceManager($q, internationalization, http) {
	const cache = { services: {} };

	return {
		deleteObjects: deleteObjects,
		getOperation: getOperation,
		getOperationInfo: getOperationInfo,
		getOperationTypes: getOperationTypes,
		getObjectInfo: getObjectInfo,
		getFragmentDescriptors: getFragmentDescriptors,
		getListExecuteOptions: getListExecuteOptions,
		getServiceInfo: getServiceInfo,
		parseServiceInfo: parseServiceInfo,
		getEntity: getEntity,
		updateEntity: updateEntity,
		isService: isService,
		getServiceId: getServiceId,
		getServiceInfoByEntityName: getServiceInfoByEntityName,
		getEnumeration: getEnumeration,
	};

	function deleteObjects(type, objects) {
		return getServiceInfoByEntityName(type).then(function (service) {
			var deleteOperation = service.Operations.filter(function (op) {
				return op.SupportedOperation === SupportedOperations.DeleteObject;
			})[0];

			if (!deleteOperation) {
				return;
			}

			var serviceURl = service.Prefix + '/';
			var path = deleteOperation.RouteTemplate.toLocaleLowerCase();

			var results = objects.map(function (object) {
				var _path = path.replace(/{\w+}/gi, object._id);
				return http.host(service.BaseUrl).remove(serviceURl + _path);
			});

			return $q.all(results);
		});
	}

	function getOperationInfo(entityName, operation) {
		var serviceId = getServiceId(entityName);
		return getServiceInfo(serviceId).then(function (service) {
			var readOperation = getOperation(service, operation);
			return getOperationTypesInternal(readOperation, entityName, serviceId, operation);
		});
	}

	function getObjectInfo(entityName, objectId) {
		var serviceId = getServiceId(entityName);

		return getServiceInfo(serviceId).then(function (service) {
			var readOperation = getOperation(service, SupportedOperations.ReadObject);

			if (!readOperation) {
				return;
			}
			var typeInfo = getResponseTypeInfo(readOperation, serviceId);

			var serviceURl = service.Prefix + '/';

			var path = readOperation.RouteTemplate.toLocaleLowerCase().replace('{id}', objectId).replace('{identifier}', objectId);

			return http
				.host(service.BaseUrl)
				.get(serviceURl + path)
				.then(function (result) {
					var item = result;

					return {
						Id: item[typeInfo.Identifier],
						DisplayString: item[typeInfo.DisplayName],
						'Sys-Entity': entityName,
					};
				});
		});
	}

	function getOperationTypeInfo(operation, serviceId, supportedOperation, subCollection) {
		if (!operation) {
			return {};
		}

		if (!serviceId) {
			serviceId = operation.ServiceId;
		}

		supportedOperation = supportedOperation || SupportedOperations.ReadObject;

		return supportedOperation === SupportedOperations.InsertObject || supportedOperation === SupportedOperations.UpdateObject
			? getRequestBodyTypeInfo(operation, serviceId)
			: getResponseTypeInfo(operation, serviceId, subCollection);
	}

	function getOperationTypes(operation, subCollection) {
		return getOperationTypesInternal(operation, undefined, undefined, undefined, subCollection);
	}

	function getOperationTypesInternal(operation, entityName, serviceId, supportedOperation, subCollection) {
		var typeInfo = getOperationTypeInfo(operation, serviceId, supportedOperation, subCollection);

		if (!entityName) {
			entityName = 'service_' + serviceId;
		}

		var oi = {
			isService: true,
			Enums: {},
			EnumTypes: [],
			EnumNames: [],
			Identifier: typeInfo.Identifier,
			TypeInfo: typeInfo,
		};

		oi.Enums.$find = function (name, value) {
			var _enum = oi.Enums[name];
			var enumField = _.find(_enum, { Value: value });
			return (enumField && enumField.DisplayString) || value;
		};

		_.forOwn(typeInfo.UsedTypes, function (type, name) {
			if (type.Enum) {
				var enumName = (entityName ? entityName + '_' : '') + name;
				oi.Enums[enumName] = [];
				_.forOwn(type.Enum, function (val) {
					oi.Enums[enumName].push({
						Value: val.Value,
						DisplayString: val.Title,
						Order: val.Order,
					});
				});

				oi.Enums[enumName] = oi.Enums[enumName].sort(function (a, b) {
					if (isNaN(a.Order) && isNaN(b.Order)) {
						return a.DisplayString.localeCompare(b.DisplayString);
					}
					if (isNaN(a.Order)) {
						return 1;
					}
					if (isNaN(b.Order)) {
						return -1;
					}
					return a.Order - b.Order;
				});

				oi.EnumNames.push(name);
				oi.EnumTypes.push({
					Name: enumName,
					DisplayName: name,
				});
			}
		});

		return oi;
	}

	function getFragmentDescriptors(request, searchText) {
		var serviceId = getServiceId(request.entityClass);

		return getServiceInfo(serviceId).then(function (service) {
			var listOperation = getOperation(service, SupportedOperations.ReadObjectsByODataQuery);

			if (!listOperation) {
				return [];
			}

			var serviceURl = service.Prefix + '/';
			var path = listOperation.RouteTemplate.toLocaleLowerCase();

			var typeInfo = getResponseTypeInfo(listOperation, serviceId);
			var url = serviceURl + path + '?$filter=contains(' + typeInfo.DisplayName + ",'" + searchText + "')";

			var objectMapper = new ObjectMapper(serviceId, typeInfo);
			var contract = listOperation.ResponseContract.ContractDescriptor;
			var getDataFromObject = function (data) {
				return contract.PathToData ? data[contract.PathToData] : data;
			};

			return http
				.host(service.BaseUrl)
				.get(url)
				.then(function (result) {
					var data = getDataFromObject(result);

					return data.map(function (item) {
						item = objectMapper(item);
						item.Id = item[typeInfo.Identifier];
						item.DisplayString = item[typeInfo.DisplayName];
						return item;
					});
				});
		});
	}

	function getOperation(service, operationFlag, operationId) {
		var ops = service.Operations.filter(function (op) {
			return (operationId && op.Id === operationId) || (!operationId && op.SupportedOperation & operationFlag);
		});

		if (ops.length === 0) {
			return;
		}
		return ops[0];
	}

	function updateEntity(entityName, objectId, entity) {
		var serviceId = getServiceId(entityName);

		return getServiceInfo(serviceId).then(function (service) {
			var isNew = objectId === 'new';
			var serviceURl = service.Prefix + '/';
			var operation = isNew ? SupportedOperations.InsertObject : SupportedOperations.UpdateObject;
			var updateOperation = getOperation(service, operation);
			var typeInfo = getRequestBodyTypeInfo(updateOperation, serviceId);
			var objectMapper = new ObjectMapper(serviceId, typeInfo);
			var path = isNew ? updateOperation.RouteTemplate || '' : getPath(updateOperation, typeInfo, objectId);
			return http
				.host(service.BaseUrl)
				.post(serviceURl + path, entity, {}, {}, true)
				.then(function (result) {
					if (isNew && result) {
						var resultId = result[typeInfo.Identifier];
						if (resultId) {
							entity[typeInfo.Identifier] = resultId;
						} else {
							entity[typeInfo.Identifier] = result;
						}
						objectMapper(entity);
					}

					return entity[typeInfo.Identifier];
				});
		});
	}

	function getEntity(entityName, objectId) {
		var serviceId = getServiceId(entityName);

		return getServiceInfo(serviceId).then(function (service) {
			var serviceURl = service.Prefix + '/';

			var getObjectOperation = getOperation(service, SupportedOperations.ReadObject);
			var typeInfo = getResponseTypeInfo(getObjectOperation, serviceId);
			var objectMapper = new ObjectMapper(serviceId, typeInfo);

			if (objectId === 'new') {
				return $q.when({
					object: objectMapper({ 'Sys-IsNew': true }),
					entityName: getObjectOperation.ResponseContract.ContractDescriptor.Type,
				});
			}

			var path = getPath(getObjectOperation, typeInfo, objectId);
			return http
				.host(service.BaseUrl)
				.get(serviceURl + path)
				.then(function (result) {
					result['Sys-IsNew'] = false;
					result = objectMapper(result);

					return {
						object: result,
						entityName: getObjectOperation.ResponseContract.ContractDescriptor.Type,
						typeName: typeInfo.TypeName,
					};
				});
		});
	}

	function getPath(operationInfo, typeInfo, objectId) {
		var path = operationInfo.RouteTemplate.toLocaleLowerCase();
		if (typeInfo.Identifier) {
			path = path.replace('{' + typeInfo.Identifier.toLocaleLowerCase() + '}', objectId);
		}
		if (!typeInfo.Identifier || path.indexOf('{') !== -1) {
			path = path.replace('{id}', objectId).replace('{identifier}', objectId);
		}
		return path;
	}

	function getListExecuteOptions(dataQuery, operation, entityService) {
		var columns = dataQuery.Columns;
		var serviceInfo = dataQuery.ServiceInfo;
		var serviceId = dataQuery.Service;
		var subCollection = dataQuery.SubCollection;
		var executeOptions = {};
		var isOData = (operation & SupportedOperations.ReadObjectsByODataQuery) > 0;

		executeOptions.serviceUrl = serviceInfo.Prefix + '/';

		var listOperation = getOperation(serviceInfo, operation, dataQuery.Operation);
		var contract = listOperation.ResponseContract.ContractDescriptor;

		executeOptions.listUrl = listOperation.RelativePath;

		executeOptions.httpProvider = http.host(serviceInfo.BaseUrl);
		executeOptions.processResponse = function (result) {
			return result;
		};

		var path = subCollection || contract.PathToData;
		executeOptions.getDataFromObject = function (data) {
			return path ? data[path] : data;
		};
		executeOptions.getProcessedDataFromObject = function (obj) {
			var data = executeOptions.getDataFromObject(obj);
			if (Array.isArray(data)) {
				return data.map(function (item) {
					return executeOptions.processDataFuncs.reduce(function (accumulator, func) {
						return func(accumulator);
					}, item);
				});
			}
		};

		/* Load count settings*/

		var returnCountType = (contract.ReturnCountType !== undefined && Number(contract.ReturnCountType)) || 3;

		executeOptions.countOptions = {
			executeExternalService: returnCountType === 3,
			dataRequestUrlCount:
				executeOptions.listUrl.indexOf('/?') !== -1
					? executeOptions.listUrl.replace('/?', '/$count?')
					: executeOptions.listUrl.indexOf('?') !== -1
					? executeOptions.listUrl.replace('?', '/$count?')
					: executeOptions.listUrl[executeOptions.listUrl.length - 1] === '/'
					? executeOptions.listUrl + '$count'
					: executeOptions.listUrl + '/$count',
			processResponse: function (result) {
				return result;
			},
		};

		if (returnCountType === 1) {
			// TODO: rework for get count from path same as data.xxx.rrr.ddd
			executeOptions.countOptions.getFromObject = function (data) {
				return data[contract.PathToCount];
			};
		}

		/* End: Load count settings*/

		/* Load Filters*/
		executeOptions.processDataFuncs = [];
		executeOptions.searchByKeywordFuncs = [];

		executeOptions.searchByKeywordFuncs.processKeyword = function (keyword) {
			if (!keyword) {
				return;
			}
			keyword = keyword.toLowerCase();
			return executeOptions.searchByKeywordFuncs
				.map(function (func) {
					return func(keyword);
				})
				.filter(function (item) {
					return Boolean(item);
				})
				.join(' or ');
		};

		var enumColumns = columns.filter(function (column) {
			return column.ReferenceTo !== undefined && column.AttributeSearchValueType === 'IntType' && column.IsKeyword;
		});

		enumColumns.forEach(function (column) {
			var enumFunctions = new ProcessEnum(column, listOperation, entityService);
			enumFunctions.then(function (enumF) {
				if (!enumF.intToString) {
					return;
				}

				executeOptions.processDataFuncs.push(enumF.intToString);
				if (isOData) {
					executeOptions.searchByKeywordFuncs.push(enumF.searchByKeyword);
				}
			});
		});

		if (isOData) {
			var stringColumns = columns.filter(function (column) {
				return column.AttributeSearchValueType === 'StringType' && !column.IsArray && column.IsKeyword;
			});

			stringColumns.forEach(function (column) {
				var expressionField = (column.SearchExpression || column.Expression).replace(/\./g, '/');
				if (!expressionField.match(/[^\w$_/]/g)) {
					executeOptions.searchByKeywordFuncs.push(function (keyword) {
						return 'contains(tolower(' + expressionField + "),'" + encodeURIComponent(escapeSql(keyword)) + "')";
					});
				}
			});
		}
		executeOptions.typeInfo = getResponseTypeInfo(listOperation, serviceId, subCollection);
		executeOptions.processDataFuncs.push(new ObjectMapper(serviceId, executeOptions.typeInfo));
		executeOptions.processDataFuncs.push(new DataQueryMapper(dataQuery, executeOptions.typeInfo));

		return executeOptions;
	}

	function escapeSql(keyword) {
		return keyword && keyword.replace(/'/g, "''''");
	}

	function DataQueryMapper(dataQuery, typeInfo) {
		var columns = dataQuery.Columns;
		if (!columns.length) {
			return function (item) {
				return item;
			};
		}

		var preFunc = typeInfo.MakeExpression();
		columns.forEach(function (c) {
			if (c.Expression && c.Expression.match(/[^\w$_]/g)) {
				c.ExpressionFunc = preFunc(c.Expression);
			}
		});

		return function (item) {
			var result = {
				_type: item._type,
				_id: item._id,
				_displayName: item._displayName,
				_getTypeDisplayName: item._getTypeDisplayName,
				_name: item._name,
			};

			columns.forEach(function (c) {
				if (c.ExpressionFunc) {
					result[c.Name] = c.ExpressionFunc(item);
				} else if (c.Expression.indexOf('.') === -1) {
					result[c.Name || c.Expression] = item[c.Expression];
				} else {
					var tmpObj = item;
					c.Expression.split('.').forEach(function (part) {
						tmpObj = tmpObj && tmpObj[part];
					});
					result[c.Name] = tmpObj;
				}
			});

			return result;
		};
	}

	function ObjectMapper(serviceId, typeInfo) {
		if (!typeInfo) {
			return function (item) {
				return item;
			};
		}

		return function (item) {
			item._type = typeInfo.Dynamic ? item[typeInfo.TypeName] : typeInfo.TypeName;
			item._id = item[typeInfo.Identifier] ? item[typeInfo.Identifier] : null;
			item._displayName = item[typeInfo.DisplayName] ? item[typeInfo.DisplayName] : internationalization.notAvailable();
			item._name = item._displayName;
			item._getTypeDisplayName = typeInfo.GetTypeDisplayNamePromise && typeInfo.GetTypeDisplayNamePromise(item);
			return item;
		};
	}

	function getRequestBodyTypeInfo(operation, serviceId) {
		var usedTypes = operation.RequestContract.UsedTypes;
		var requestContractDescriptor = usedTypes.RequestContract.Descriptor;
		var contractDescriptor = _.find(requestContractDescriptor, { Source: 1 });
		var resultType = contractDescriptor.Type;
		return getTypeInfo(usedTypes, resultType, serviceId);
	}

	function getResponseTypeInfo(operation, serviceId, subCollection) {
		var contractDescriptor = operation.ResponseContract.ContractDescriptor;
		var usedTypes = operation.ResponseContract.UsedTypes;
		var resultTypeObj;

		var resultType = contractDescriptor.Type;

		if (Array.isArray(resultType)) {
			resultType = resultType[0];
		}

		if (subCollection || contractDescriptor.PathToData) {
			resultTypeObj = usedTypes[resultType];
			if (!resultTypeObj) {
				return;
			}
			resultType = resultTypeObj.Descriptor[subCollection || contractDescriptor.PathToData].Type;
		}
		return getTypeInfo(usedTypes, resultType, serviceId);
	}

	function getTypeInfo(usedTypes, resultType, serviceId) {
		const resultTypeObj = usedTypes[resultType];
		if (!resultTypeObj) {
			return;
		}

		let typeName = resultTypeObj.TypeName;

		if (!typeName && serviceId) {
			typeName = 'service_' + serviceId;
		}

		const typeDisplayNameAttr = resultTypeObj.TypeDisplayName;
		let typeDisplayName;

		switch (typeDisplayNameAttr?.DisplayType) {
			case 0: //field
				typeDisplayName = function (item) {
					return $q.when(item?.[typeDisplayNameAttr?.Name]);
				};
				break;
			case 1: //static
				typeDisplayName = function () {
					return $q.when(typeDisplayNameAttr?.Name);
				};
				break;
			case 2: //TODO: from service
				typeDisplayName = function () {
					return $q.when(typeDisplayNameAttr?.Name);
				};
				break;

			case 3: //TODO: from service
				typeDisplayName = function () {
					return $q.when(typeDisplayNameAttr?.Name);
				};
				break;
			default:
				//TODO: from service
				typeDisplayName = function () {
					return $q.when(typeDisplayNameAttr?.Name);
				};
				break;
		}

		return {
			Identifier: resultTypeObj.Identifier,
			DisplayName: resultTypeObj.DisplayName,
			Dynamic: resultTypeObj.Dynamic === true,
			TypeName: typeName,
			GetTypeDisplayNamePromise: typeDisplayName,
			UsedTypes: usedTypes,
			ResultType: resultTypeObj,
			MakeExpression: makeExpression(resultTypeObj, usedTypes),
		};
	}

	function findMainCollection(type, usedTypes, pathToObject) {
		if (!pathToObject) {
			return type;
		}
		var innerType = type.Descriptor[pathToObject].Type;
		if (Array.isArray(innerType)) {
			innerType = innerType[0];
		}
		return usedTypes[innerType];
	}

	function makeExpression(type, usedTypes) {
		return function (pathToObject) {
			var params = [];
			var args = [];
			_.forOwn(findMainCollection(type, usedTypes, pathToObject).Descriptor, function (value, key) {
				args.push(key);
				params.push('context.' + key);
			});

			var funcPartArgs = 'var func = function(' + args.join(',') + '){';
			var funcPartParams = '};\r\nreturn func(' + params.join(',') + ');\r\n';
			return function (expression) {
				expression = expression.replace(/\/\*.+?\*\/|\/\/.*(?=[\n\r])/g, '').trim();
				var exp = expression.match(/return(:?\s|;)/g) ? expression : 'return ' + expression;
				var sFunc = funcPartArgs + exp + funcPartParams;
				var func = new Function('context', sFunc);
				return function (object) {
					return func(object);
				};
			};
		};
	}

	function ProcessEnum(column, operation, entityService) {
		var defer = $q.defer();

		var enumName = column.ReferenceTo;

		if (!enumName) {
			return;
		}
		var field = column.Name;
		var expressionField = column.SearchExpression || column.Expression;
		var enumInfo = operation.ResponseContract.UsedTypes[enumName];
		if (!enumInfo) {
			entityService.getEnumerationValues(enumName).then(function (enumData) {
				defer.resolve(_processEnum({ Enum: enumData }, field, expressionField));
			});
		} else {
			defer.resolve(_processEnum(enumInfo, field, expressionField));
		}

		return defer.promise;
	}

	function _processEnum(enumInfo, field, expressionField) {
		var _enum = enumInfo.Enum;
		var flags = enumInfo.Flags;
		var enumArray = [];
		var enumArrayLowerCase = [];

		Object.keys(_enum).forEach(function (paramName) {
			var value = _enum[paramName];

			enumArray[value.Value] = value.Title || value.DisplayString;
			enumArrayLowerCase[value.Value] = (value.Title || value.DisplayString).replace(/\s/g, '').toLowerCase();
		});

		return {
			intToString: function (item) {
				var val = item[expressionField];
				if (flags) {
					var strings = [];
					var pos = 1;
					var flagValue;
					for (flagValue = pos & val; pos <= val; pos = pos << 1, flagValue = pos & val) {
						if (flagValue) {
							strings.push(enumArray[flagValue]);
						}
					}
					item[field] = strings.join(', ');
				} else {
					item[field] = enumArray[val];
				}
				return item;
			},
			searchByKeyword: function (keyword) {
				var matchingValues = [];

				enumArray.forEach(function (enumItem, index) {
					if (enumItem.indexOf(keyword) !== -1 || enumItem.toLowerCase().indexOf(keyword) !== -1) {
						if (_enum.NameSpace) {
							matchingValues.push(String(expressionField) + _enum.NameSpace + "'" + index + "'");
						} else {
							matchingValues.push(String(expressionField) + ' eq ' + index);
						}
					}
				});
				return matchingValues.join(' or ');
			},
		};
	}

	function isService(entityName) {
		return typeof entityName === 'string' && entityName.indexOf('service_') === 0;
	}

	function getServiceId(entityName) {
		return entityName.split('_')[1];
	}

	function getServiceInfo(serviceId) {
		var result = cache.services[serviceId];

		if (!result) {
			result = http.get('api/Help/ServiceInfo/' + serviceId).then();
			cache.services[serviceId] = result;
		}
		return result;
	}

	function getServiceInfoByEntityName(entityName) {
		return getServiceInfo(getServiceId(entityName));
	}

	function parseServiceInfo(serviceData) {
		var serviceContext = {
			Operations: {},
			OperationTypes: {},
			Enums: {},
			UsedTypes: {},
		};

		serviceData.Operations.forEach(function (operation) {
			serviceContext.Operations[operation.Id] = operation;
		});
		serviceContext.UsedTypes = serviceData.Operations.reduce(function (usedTypes, operation) {
			return angular.extend(usedTypes, getAllUsedTypes(operation));
		}, {});

		serviceContext.Enums = getEnums(serviceContext.UsedTypes);

		return serviceContext;
	}

	function getServiceContext(serviceId) {
		return getServiceInfo(serviceId).then(parseServiceInfo);
	}

	function getAllUsedTypes(operation) {
		var usedTypes = {};

		Object.keys(operation.ResponseContract.UsedTypes).forEach(function (typeName) {
			usedTypes[typeName] = operation.ResponseContract.UsedTypes[typeName];
		});

		Object.keys(operation.RequestContract.UsedTypes).forEach(function (typeName) {
			usedTypes[typeName] = operation.RequestContract.UsedTypes[typeName];
		});

		return usedTypes;
	}

	function getEnums(usedTypes) {
		var enums = {};
		Object.keys(usedTypes).forEach(function (enumName) {
			var type = usedTypes[enumName];

			if (type.Enum) {
				enums[enumName] = {
					Name: enumName,
					Values: [],
				};

				Object.keys(type.Enum).forEach(function (name) {
					var val = type.Enum[name];

					enums[enumName].Values.push({
						Value: val.Value,
						DisplayString: val.Title,
						Order: val.Order,
					});
				});

				enums[enumName].Values.sort(function (a, b) {
					if (isNaN(a.Order) && isNaN(b.Order)) {
						return a.DisplayString.localeCompare(b.DisplayString);
					}
					if (isNaN(a.Order)) {
						return 1;
					}
					if (isNaN(b.Order)) {
						return -1;
					}
					return a.Order - b.Order;
				});
			}
		});

		return enums;
	}

	function getEnumName(fullName) {
		if (typeof fullName === 'string') {
			return fullName.split('_')[2] || null;
		}
		return null;
	}

	function getEnumeration(name) {
		if (!isService(name)) {
			return $q.reject(name + ' is not a service');
		}
		var serviceId = getServiceId(name);
		var enumName = getEnumName(name);
		return getServiceContext(serviceId).then(function (serviceContext) {
			return serviceContext.Enums[enumName];
		});
	}
}
